import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FocusLayout, MediaDeviceSelect, RoomAudioRenderer, TrackLoop, useLocalParticipant, useParticipantTracks, useTrackToggle, } from '@livekit/components-react';
import '@livekit/components-styles';
import '@livekit/components-styles/prefabs';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Track } from 'livekit-client';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { BtwButton, BtwHeading, BtwModal, BtwPopover, BtwText, BtwTooltip, ParticipantGrid, ParticipantTile, Stack, cn, } from '@marvelapp/ballpark-components';
import '@marvelapp/ballpark-components/src/Live/Global.css';
import '@marvelapp/ballpark-components/src/Live/Participant.css';
import { ChevronBottomSmallIcon, MicrophoneDisabledIcon, MicrophoneIcon, ScreenDisabledIcon, ScreenIcon, WebcamDisabledIcon, WebcamIcon, } from '@marvelapp/ballpark-icons';
import theme from '@marvelapp/ballpark-theme';
import { useMainParticipantTracks } from '@marvelapp/hooks';
import { ConnectionQualityMonitor } from './ConnectionQualityMonitor';
import { EnableKrisp } from './EnableKrisp';
import { useRecorderState } from './RecorderContext';
import { LiveSessionStatus } from './useLiveSessionStatus';
export const ParticipantRoom = observer(function ParticipantRoom() {
    const { isLive, liveSessionStatus } = useRecorderState();
    if (!isLive) {
        return null;
    }
    if (liveSessionStatus === LiveSessionStatus.Lobby) {
        return (_jsx(_Fragment, { children: _jsx(BtwModal.Root, { open: true, children: _jsx(BtwModal.Content, { position: "full", size: "full", onOpenAutoFocus: (event) => {
                        event.preventDefault();
                    }, children: _jsxs(BtwModal.Body, { className: "lk-waiting-room flex h-screen w-screen flex-col items-center justify-center p-10", children: [_jsxs(Stack, { align: "center", gap: "2", className: "h-full flex-1", children: [_jsx(BtwHeading, { size: "xl", weight: "bold", variant: "secondary", children: "Please wait\u2026" }), _jsx(BtwText, { size: "base", weight: "normal", children: "Waiting for researchers to join and start the session\u2026" })] }), _jsxs(LiveKitComponentsContainer, { children: [_jsx(Grid, {}), _jsx(ConnectionQualityMonitor, {})] }), _jsx(Stack, { gap: "2", direction: "row", width: "full", align: "center", justify: "center", children: _jsx(MediaControls, {}) })] }) }) }) }));
    }
    if (liveSessionStatus === LiveSessionStatus.Recording) {
        return (_jsxs(LiveKitComponentsContainer, { children: [_jsx(RecordingView, {}), _jsx(ConnectionQualityMonitor, {})] }));
    }
    return null;
});
const LiveKitComponentsContainer = observer(function LiveKitComponentsContainer({ children, }) {
    return (_jsxs(_Fragment, { children: [_jsx(RoomAudioRenderer, {}), children] }));
});
const slideAnimationVariants = {
    initial: (direction) => ({
        y: direction * 100,
        opacity: 0,
    }),
    animate: {
        y: 0,
        opacity: 1,
    },
    exit: (direction) => ({
        y: direction * -100,
        opacity: 0,
    }),
};
const transition = {
    duration: 0.3,
    ease: theme.easings.smooth.array,
};
const Carousel = observer(function Carousel() {
    const cameraTracks = useNonObserverTracks();
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(0);
    const [direction, setDirection] = useState(1);
    const containerRef = useRef(null);
    const shouldReduceMotion = useReducedMotion();
    // Calculate items per page based on container height
    useEffect(() => {
        const calculateItemsPerPage = () => {
            var _a, _b;
            const containerHeight = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) !== null && _b !== void 0 ? _b : 0;
            // 60px for tile height + 8px for gap
            const itemHeight = 68;
            // Subtract space for controls (28px + 8px for gap)
            const availableHeight = containerHeight - 36;
            const calculatedItems = Math.floor(availableHeight / itemHeight);
            setItemsPerPage(Math.max(1, calculatedItems));
        };
        calculateItemsPerPage();
        window.addEventListener('resize', calculateItemsPerPage);
        return () => window.removeEventListener('resize', calculateItemsPerPage);
    }, []);
    const totalPages = Math.ceil(cameraTracks.length / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const visibleTracks = cameraTracks.slice(startIndex, startIndex + itemsPerPage);
    // Calculate remaining items on previous/next pages
    const previousPageItems = currentPage > 0 ? Math.min(itemsPerPage, startIndex) : 0;
    const nextPageItems = currentPage < totalPages - 1
        ? Math.min(itemsPerPage, cameraTracks.length - (startIndex + itemsPerPage))
        : 0;
    const goToNextPage = () => {
        setDirection(1);
        setCurrentPage((prev) => (prev + 1) % totalPages);
    };
    const goToPrevPage = () => {
        setDirection(-1);
        setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    };
    return (_jsx(Stack, { gap: "2", className: "relative flex-1 grow-0 basis-full", ref: containerRef, children: _jsx(AnimatePresence, { custom: direction, children: _jsxs(motion.div, { custom: direction, variants: !shouldReduceMotion ? slideAnimationVariants : undefined, initial: "initial", animate: "animate", exit: "exit", transition: transition, layout: true, className: "absolute bottom-0 left-0 right-0 top-0 flex flex-col gap-2", children: [currentPage > 0 && (_jsx(BtwTooltip, { content: `${previousPageItems} more above`, delayDuration: 500, collisionPadding: 4, children: _jsxs(BtwButton, { onClick: goToPrevPage, variant: "ghost", size: "sm", className: "mx-auto w-[60px] text-xs", children: ["\u2191 ", previousPageItems] }) })), _jsx(Stack, { gap: "2", children: _jsx(TrackLoop, { tracks: visibleTracks, children: _jsx("div", { className: "relative h-[60px] w-[60px] shrink-0 hover:z-[9999]", children: _jsx(ParticipantTile, { withQualityTooltip: true }) }) }) }), currentPage < totalPages - 1 && (_jsx(BtwTooltip, { content: `${nextPageItems} more below`, delayDuration: 500, collisionPadding: 4, children: _jsxs(BtwButton, { onClick: goToNextPage, variant: "ghost", size: "sm", className: "mx-auto w-[60px] text-xs", children: ["\u2193 ", nextPageItems] }) }))] }, currentPage) }) }));
});
const Grid = observer(function Grid() {
    const tracks = useNonObserverTracks();
    return _jsx(ParticipantGrid, { tracks: tracks });
});
const RecordingView = observer(function Controls() {
    return (_jsxs(Stack, { justify: "between", gap: "2", className: "lk-participant-view h-full w-[60px]", children: [_jsx(Carousel, {}), _jsx(Stack, { gap: "2", children: _jsx(MediaControls, {}) })] }));
});
const MediaControls = observer(function MediaControls() {
    const { liveSessionStatus, requiredMedia } = useRecorderState();
    const { localParticipant } = useLocalParticipant();
    const screenshareTrack = useParticipantTracks([Track.Source.ScreenShare], localParticipant.identity);
    return (_jsxs(_Fragment, { children: [_jsx(EnableKrisp, {}), !!screenshareTrack.length &&
                liveSessionStatus !== LiveSessionStatus.Lobby && (_jsx("div", { className: "relative h-[60px] w-[60px]", children: _jsx(FocusLayout, { trackRef: screenshareTrack[0], children: _jsx(ParticipantTile, { withQualityTooltip: true }) }) })), requiredMedia.screen && _jsx(ScreenShareButton, {}), requiredMedia.microphone && _jsx(MicButton, {}), requiredMedia.webcam && _jsx(CameraButton, {})] }));
});
const CameraButton = observer(function CameraButton() {
    const { buttonProps, enabled } = useTrackToggle({
        source: Track.Source.Camera,
    });
    // Omit the className from buttonProps
    const { className } = buttonProps, otherButtonProps = __rest(buttonProps, ["className"]);
    return (_jsxs(Stack, { direction: "row", className: "ease-smooth transition-transform duration-300 hover:-translate-y-px has-[:active]:translate-y-px", children: [_jsx(BtwTooltip, { content: enabled ? 'Turn off camera' : 'Turn on camera', collisionPadding: 4, children: _jsx(BtwButton, Object.assign({}, otherButtonProps, { standaloneIcon: enabled ? _jsx(WebcamIcon, {}) : _jsx(WebcamDisabledIcon, {}), variant: "secondary", size: "intermediate", className: cn('peer rounded-br-none rounded-tr-none hover:translate-y-0 active:translate-y-0 data-[lk-enabled=false]:bg-white/50') })) }), _jsxs(BtwPopover.Root, { children: [_jsx(BtwPopover.Trigger, { asChild: true, children: _jsx(BtwButton, { standaloneIcon: _jsx(ChevronBottomSmallIcon, {}), variant: "secondary", size: "intermediate", className: cn('w-7 rounded-bl-none rounded-tl-none hover:translate-y-0 active:translate-y-0 peer-data-[lk-enabled=false]:bg-white/50') }) }), _jsx(BtwPopover.Content, { collisionPadding: 8, side: "top", children: _jsx(MediaDeviceSelect, { kind: "videoinput" }) })] })] }));
});
const MicButton = observer(function MicButton() {
    const { buttonProps, enabled } = useTrackToggle({
        source: Track.Source.Microphone,
    });
    // Omit the className from buttonProps
    const { className } = buttonProps, otherButtonProps = __rest(buttonProps, ["className"]);
    return (_jsxs(Stack, { direction: "row", className: "ease-smooth transition-transform duration-300 hover:-translate-y-px has-[:active]:translate-y-px", children: [_jsx(BtwTooltip, { content: enabled ? 'Mute microphone' : 'Unmute microphone', collisionPadding: 4, children: _jsx(BtwButton, Object.assign({}, otherButtonProps, { standaloneIcon: enabled ? _jsx(MicrophoneIcon, {}) : _jsx(MicrophoneDisabledIcon, {}), variant: "secondary", size: "intermediate", className: cn('peer rounded-br-none rounded-tr-none hover:translate-y-0 active:translate-y-0 data-[lk-enabled=false]:bg-white/50') })) }), _jsxs(BtwPopover.Root, { children: [_jsx(BtwPopover.Trigger, { asChild: true, children: _jsx(BtwButton, { standaloneIcon: _jsx(ChevronBottomSmallIcon, {}), variant: "secondary", size: "intermediate", className: "w-7 rounded-bl-none rounded-tl-none hover:translate-y-0 active:translate-y-0 peer-data-[lk-enabled=false]:bg-white/50" }) }), _jsx(BtwPopover.Content, { collisionPadding: 8, side: "top", children: _jsx(MediaDeviceSelect, { kind: "audioinput" }) })] })] }));
});
const ScreenShareButton = observer(function ScreenShareButton() {
    const { buttonProps, enabled } = useTrackToggle({
        source: Track.Source.ScreenShare,
        captureOptions: {
            video: {
                displaySurface: 'monitor',
            },
        },
    });
    // Omit the className from buttonProps
    const { className } = buttonProps, otherButtonProps = __rest(buttonProps, ["className"]);
    return (_jsx(BtwTooltip, { content: enabled ? 'Stop sharing screen' : 'Share your screen', collisionPadding: 4, children: _jsx(BtwButton, Object.assign({}, otherButtonProps, { standaloneIcon: enabled ? _jsx(ScreenIcon, {}) : _jsx(ScreenDisabledIcon, {}), variant: "secondary", size: "intermediate", className: cn('data-[lk-enabled=false]:bg-white/50') })) }));
});
function useNonObserverTracks() {
    const participantTracks = useMainParticipantTracks();
    const { localParticipant } = useLocalParticipant();
    return participantTracks.filter((track) => {
        var _a;
        return (((_a = track.participant.attributes) === null || _a === void 0 ? void 0 : _a.video_role) === 'RESEARCHER' ||
            track.participant.sid === (localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.sid));
    });
}
